import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { MaskLine } from "@/lib/motion";

// Policy pages powered by WebsitePolicies' inline embed.
// The provider script (loaded once in public/index.html) scans for <a class="wpembed-inline" href=".../policies/view/ID">
// on window `load` and exposes the supported `wpembed.reinit()` to re-scan after client-side navigation.
// The anchor is created imperatively (not by React) because the script replaces the anchor node with the policy HTML.
export const POLICIES = {
  privacy: { path: "/privacy-policy", title: "Privacy Policy", id: "6ar6h702", eyebrow: "Legal", lead: "How Oakford Ledger collects, uses, and protects personal information provided through this website." },
  terms: { path: "/terms-and-conditions", title: "Terms and Conditions", id: "p6qoa3ue", eyebrow: "Legal", lead: "The terms that govern use of the oakfordledger.com website and its related services." },
  cookies: { path: "/cookie-policy", title: "Cookie Policy", id: "oolmdo2c", eyebrow: "Legal", lead: "What cookies are and how they are used on the oakfordledger.com website." },
};

const hostedUrl = (id) => `https://app.websitepolicies.com/policies/view/${id}`;

const useWpEmbed = (containerRef, id) => {
  const [status, setStatus] = useState("loading"); // loading | ready | unavailable
  // Layout effect: the container is populated before the browser paints, so a stale policy can never flash.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    setStatus("loading");
    el.innerHTML = `<a class="wpembed-inline" href="${hostedUrl(id)}">Loading policy…</a>`;

    let cancelled = false;
    let timer;
    const scan = () => {
      if (cancelled) return;
      if (window.wpembed && window.__wpembedModalInstance) window.wpembed.reinit();
      else if (window.wpembed) window.__wpembedModalInstance = new window.wpembed.modal({});
      else { timer = setTimeout(scan, 150); return; }
      timer = setTimeout(check, 150);
    };
    const check = () => {
      if (cancelled) return;
      const anchor = el.querySelector("a.wpembed-inline");
      if (!anchor) { setStatus("ready"); return; }
      timer = setTimeout(check, 250);
    };
    // Script is `defer`ed and initializes on window.load; wait for it rather than racing it.
    if (document.readyState === "complete") scan();
    else window.addEventListener("load", scan, { once: true });
    const giveUp = setTimeout(() => { if (!cancelled && el.querySelector("a.wpembed-inline")) setStatus("unavailable"); }, 10000);

    return () => { cancelled = true; clearTimeout(timer); clearTimeout(giveUp); window.removeEventListener("load", scan); el.innerHTML = ""; };
  }, [containerRef, id]);
  return status;
};

export default function PolicyPage({ policy }) {
  const containerRef = useRef(null);
  const { pathname } = useLocation();
  const status = useWpEmbed(containerRef, policy.id);
  useEffect(() => { document.title = `${policy.title} — Oakford Ledger`; return () => { document.title = "Oakford Ledger — Bookkeeping for Small Business"; }; }, [policy.title]);

  return (
    <article data-testid={`policy-page-${policy.id}`} className="bg-cream pt-40 pb-28">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        <header className="max-w-3xl">
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-6">{policy.eyebrow}</p>
          <h1 className="font-serif font-medium tracking-tight leading-[1.04] text-navy text-5xl sm:text-6xl">
            <MaskLine delay={0.05}>{policy.title}</MaskLine>
          </h1>
          <p className="anim-up mt-6 text-ink-muted text-lg leading-relaxed" style={{ animationDelay: "0.3s" }}>{policy.lead}</p>
        </header>

        <nav aria-label="Policies" data-testid="policy-switcher" className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-y border-[#D9CFB8] py-3">
          {Object.values(POLICIES).map((p) => (
            <Link key={p.path} to={p.path} data-testid={`policy-switch-${p.id}`}
              className={`inline-flex items-center min-h-[44px] font-mono text-[13px] uppercase tracking-[0.12em] transition-colors ${pathname === p.path ? "text-navy border-b-2 border-[#C5A059]" : "text-ink-muted hover:text-navy"}`}>
              {p.title}
            </Link>
          ))}
        </nav>

        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div ref={containerRef} data-testid="policy-embed" data-status={status} className="policy-content" />
            {status === "unavailable" && (
              <p data-testid="policy-unavailable" className="mt-6 text-[15px] text-ink border-l-2 border-[#C5A059] pl-4">
                The policy could not be displayed here. Please open the hosted version using the link below.
              </p>
            )}
          </div>
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 bg-cream-ivory border border-[#D9CFB8] p-6">
              <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-[#8a6b2f]">Hosted copy</p>
              <p className="mt-3 text-[15px] text-ink-muted leading-relaxed">
                This policy is maintained with WebsitePolicies and displayed here in full. The hosted copy is always available.
              </p>
              <a href={hostedUrl(policy.id)} target="_blank" rel="noopener noreferrer" data-testid="policy-hosted-link"
                className="mt-4 inline-flex items-center gap-2 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.12em] text-navy link-underline">
                Open hosted {policy.title.toLowerCase()} <ExternalLink size={14} aria-hidden="true" />
              </a>
              <p className="mt-6 text-[14px] text-ink-muted">Questions? <Link to="/contact" className="text-navy link-underline">Contact us</Link>.</p>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}
